import { useContext, type ReactNode } from "react";
import { SelectionPanelPlacementContext } from "./selection-style-panel-frame";
import { TextStyleBoldButton, type TextStyleItemKey } from "./text-style-panel-model";
import { ToolbarParentChildPanel, type ToolbarParentItem } from "./toolbar-parent-child-panel";

export type SelectionStyleChildKey =
  | "sticky-color"
  | "shape-fill"
  | "shape-stroke"
  | "shape-stroke-pattern"
  | "shape-stroke-width"
  | "connector-stroke"
  | "connector-stroke-width"
  | "pen-color"
  | "pen-stroke-pattern"
  | "pen-thickness"
  // テキストの項目キーは配置前パネルと共有し、片方だけへ項目が増える状態を作らない。
  | TextStyleItemKey;

export type SelectionStyleParentItem = ToolbarParentItem<SelectionStyleChildKey>;

/**
 * 選択中オブジェクト用の親子パネル。開閉・ARIA・dismiss などの共通動作は
 * ToolbarParentChildPanel が持ち、ここでは選択時固有の識別子と「太字」の直接操作だけを組み立てる。
 */
export function SelectionStyleParentPanel({
  label,
  items,
  renderChild,
  bold,
  onToggleBold,
  extraActions,
}: {
  readonly label: string;
  readonly items: readonly SelectionStyleParentItem[];
  readonly renderChild: (key: SelectionStyleChildKey) => ReactNode;
  readonly bold?: boolean | null | undefined;
  readonly onToggleBold?: (() => void) | undefined;
  /** 子パネルを開かない直接操作（表の行・列の増減など）。太字ボタンと同じ列へ並べる。 */
  readonly extraActions?: ReactNode | undefined;
}): JSX.Element {
  // 選択図形に対する親の表示側をそのまま子の端揃え・展開側の入力にする。
  const parentSide = useContext(SelectionPanelPlacementContext)?.side ?? "bottom";
  return (
    <ToolbarParentChildPanel<SelectionStyleChildKey>
      label={label}
      items={items}
      renderChild={renderChild}
      parentSide={parentSide}
      testIdPrefix="selection-style"
      directActions={
        onToggleBold || extraActions ? (
          <>
            {onToggleBold ? (
              <TextStyleBoldButton
                pressed={bold === null ? null : bold === true}
                testId="selection-style-bold"
                shortcut="Ctrl+B"
                onToggle={onToggleBold}
              />
            ) : null}
            {extraActions}
          </>
        ) : undefined
      }
    />
  );
}
